import type React from "react";
import { useCallback, useEffect, useState } from "react";

import Utils from "byzantine/src/utils";
import ApiHttp from "byzantine/src/ApiHttp";
import { useObjectMemo } from "byzantine";

import type App from "byzantine/src/App";

export const useAppLaunch = (nafApp: App, autoLaunch = false) => {
  const [promptAuthorize, setPromptAuthorize] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);

  const launch = useCallback(
    (token: string) => {
      if (!nafApp.has_own_layout) {
        window.location.href = `/apps/${nafApp.client_id}`;
      } else {
        ApiHttp.fetch(`signed_urls/apps/apps/${nafApp.client_id}`).then(
          (response: { url: string }) => {
            if (autoLaunch) {
              window.location.href = Utils.appendQuerystringParam(
                response.url,
                "token",
                token
              ).href;
            } else {
              window.open(
                Utils.appendQuerystringParam(response.url, "token", token).href,
                nafApp.name
              );
            }
          }
        );
      }
    },
    [nafApp, autoLaunch]
  );

  const handleClick = useCallback(() => {
    if (isAuthorizing) return;
    setIsAuthorizing(true);
    nafApp
      .getAuthorization()
      .then(({ token }) => {
        launch(token);
      })
      .catch(() => {
        setPromptAuthorize(true);
        setIsLoading(false);
      })
      .finally(() => {
        setIsAuthorizing(false);
      });
  }, [nafApp, isAuthorizing, launch]);

  const handleAuthorize = useCallback(() => {
    if (isAuthorizing) return;
    setIsAuthorizing(true);
    nafApp
      .createAuthorization()
      .then(({ token }) => {
        launch(token);
      })
      .catch(() => {})
      .finally(() => {
        setIsAuthorizing(false);
      });
  }, [nafApp, isAuthorizing, launch]);

  useEffect(() => {
    if (autoLaunch) {
      setIsLoading(true);
      handleClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLaunch]);

  const onUserDismiss = (event: React.MouseEvent) => {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    setPromptAuthorize(false);
  };

  return useObjectMemo({
    handleClick,
    handleAuthorize,
    onUserDismiss,
    isLoading,
    promptAuthorize,
  });
};
